import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BookOpen, ArrowRight, Quote, Share2 } from 'lucide-react';
import { issueSlug } from '../lib/issueSlug';
import IssueCarousel from '../components/IssueCarousel';
import { Reveal } from '../components/Reveal';
import { useContent } from '../context/ContentContext';
import FlipbookModal from '../components/FlipbookModal';

function SectionTitle({ eyebrow, title, action }) {
  return (
    <div className="flex items-end justify-between mb-8 gap-6">
      <div>
        {eyebrow && <p className="eyebrow text-[#D01919] mb-3">{eyebrow}</p>}
        <h2 className="font-display text-4xl md:text-5xl">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export default function Magazine() {
  const {
    coverStory, authoredArticles, spotlights, whatMadeNews,
    industryReport, enersiderBrief, enersiderView, newsmaker, editions, digitalEditionUrl,
  } = useContent();
  const currentIssue = authoredArticles[0]?.sourceIssue;
  const issueArticles = currentIssue ? authoredArticles.filter((a) => a.sourceIssue === currentIssue) : authoredArticles;
  const latest = editions.find((e) => e.latest) || editions[0];
  const archive = editions.filter((e) => e !== latest);
  const [readUrl, setReadUrl] = useState(null);
  const [readTitle, setReadTitle] = useState('');
  const openIssue = (e) => { if (e.pdfUrl) { setReadUrl(e.pdfUrl); setReadTitle(`${e.title} · ${e.month}`); } };

  return (
    <div className="pt-32 md:pt-40 pb-24">
      {/* Header */}
      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="eyebrow text-[#D01919] mb-4">The Enersider Magazine</p>
                <h1 className="font-display text-6xl md:text-8xl leading-none">Every issue.</h1>
              </div>
              <Link to="/editions" className="inline-flex items-center gap-2 bg-[#111111] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors">
                <BookOpen size={18} /> Open the reader
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Latest issue */}
      {latest && (
        <section className="section-pad py-14">
          <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <Reveal>
              <button onClick={() => openIssue(latest)} className="group relative block w-full max-w-md mx-auto lg:mx-0" data-testid="magazine-latest-cover">
                <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[3/4] ring-1 ring-black/5">
                  {latest.cover ? <img src={latest.cover} alt={latest.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <div className="w-full h-full bg-[#f4f4f4]" />}
                </div>
                {latest.pdfUrl && <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30"><span className="inline-flex items-center gap-2 bg-white text-[#111111] font-medium px-5 py-3 rounded-full"><BookOpen size={18} /> Read the Issue</span></span>}
              </button>
            </Reveal>
            <Reveal delay={120}>
              <div>
                <p className="eyebrow text-[#D01919] mb-4">Current edition · {latest.month}</p>
                <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">{latest.title}</h2>
                {latest.summary ? <p className="text-[#666666] text-lg mt-5 leading-relaxed">{latest.summary}</p> : null}
                <div className="flex flex-wrap gap-3 mt-8">
                  {latest.pdfUrl && <button onClick={() => openIssue(latest)} data-testid="magazine-read-issue" className="inline-flex items-center gap-2 bg-[#111111] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors"><BookOpen size={18} /> Read the Issue</button>}
                  <Link to="/editions" className="inline-flex items-center gap-1.5 border border-[#111111]/20 font-medium px-6 py-3.5 rounded-full hover:bg-[#f4f4f4] transition-colors">All editions <ArrowRight size={17} /></Link>
                  <Link to={`/magazine/${issueSlug(latest)}`} data-testid="magazine-latest-share" className="inline-flex items-center gap-1.5 border border-[#111111]/20 font-medium px-6 py-3.5 rounded-full hover:bg-[#f4f4f4] transition-colors"><Share2 size={17} /> Share this issue</Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Archive */}
      {archive.length > 0 && (
        <section className="section-pad py-14">
          <div className="max-w-[1500px] mx-auto">
            <SectionTitle eyebrow="The archive" title="Back issues" />
            <IssueCarousel items={archive} testid="magazine-archive" renderItem={(e) => (
                <div>
                  <button onClick={() => openIssue(e)} className="group text-left w-full" data-testid={`archive-issue-${issueSlug(e)}`}>
                    <div className="rounded-2xl overflow-hidden aspect-[3/4] mb-3 shadow-lg ring-1 ring-black/5">
                      {e.cover ? <img src={e.cover} alt={e.title} draggable={false} className="card-img w-full h-full object-cover" /> : <div className="w-full h-full bg-[#f4f4f4]" />}
                    </div>
                    <p className="eyebrow text-[#666666]">{e.month}</p>
                    <h4 className="font-display text-lg mt-1 group-hover:text-[#D01919] transition-colors">{e.title}</h4>
                  </button>
                  <Link to={`/magazine/${issueSlug(e)}`} data-testid={`archive-issue-link-${issueSlug(e)}`} className="inline-flex items-center gap-1 text-xs text-[#666666] hover:text-[#D01919] mt-1.5"><Share2 size={12} /> Issue page &amp; share</Link>
                </div>
              )} />
          </div>
        </section>
      )}

      {/* Cover Story (shown only when the editor has published one) */}
      {coverStory && (
        <section className="section-pad py-16">
          <div className="max-w-[1500px] mx-auto">
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div className="relative rounded-3xl overflow-hidden aspect-[16/12]">
                  {coverStory.image ? <img src={coverStory.image} alt={coverStory.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#f4f4f4]" />}
                  <span className="absolute top-5 left-5 bg-[#D01919] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">Cover Story</span>
                </div>
                <div>
                  {coverStory.eyebrow ? <p className="eyebrow text-[#D01919] mb-4">{coverStory.eyebrow}</p> : null}
                  <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">{coverStory.title}</h2>
                  {coverStory.dek ? <p className="text-[#666666] text-lg mt-5 leading-relaxed">{coverStory.dek}</p> : null}
                  {coverStory.body && coverStory.body[0] ? <p className="text-[#111111] mt-5 leading-relaxed">{coverStory.body[0]}</p> : null}
                  {coverStory.editor ? (
                    <div className="flex items-center gap-3 mt-7">
                      <div className="w-11 h-11 rounded-full bg-[#111111] text-[#D01919] flex items-center justify-center font-display text-lg">{coverStory.editor.charAt(0)}</div>
                      <div><p className="font-medium">{coverStory.editor}</p><p className="text-sm text-[#666666]">{coverStory.editorRole}</p></div>
                    </div>
                  ) : null}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Enersider View (editorial column) */}
      {enersiderView && (
        <section className="section-pad py-16 bg-[#111111] text-white">
          <div className="max-w-[1100px] mx-auto">
            <Reveal>
              <p className="eyebrow text-[#D01919] mb-4">{enersiderView.eyebrow || 'Enersider View'}</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.03]">{enersiderView.title}</h2>
              {enersiderView.author ? (
                <p className="text-white/60 mt-4 text-sm uppercase tracking-wider">By {enersiderView.author}{enersiderView.authorRole ? `, ${enersiderView.authorRole}` : ''}</p>
              ) : null}
              <div className="mt-7 space-y-5 max-w-3xl">
                {(enersiderView.body || []).map((p, i) => (
                  <p key={i} className="text-white/80 text-lg leading-relaxed">{p}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Authored Articles */}
      {issueArticles.length > 0 && (
        <section className="section-pad py-16">
          <div className="max-w-[1500px] mx-auto">
            <SectionTitle eyebrow="In this edition" title="Authored Articles" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {issueArticles.map((a, i) => (
                <Reveal key={a.id} delay={(i % 3) * 80}>
                  <Link to={`/article/${a.slug}`} className="group flex flex-col h-full border-t-2 border-[#111111] pt-5 hover:border-[#D01919] transition-colors" data-testid="authored-card">
                    <p className="eyebrow text-[#D01919] mb-2">{a.category}</p>
                    <h3 className="font-display text-xl leading-snug group-hover:text-[#D01919] transition-colors">{a.title}</h3>
                    {a.excerpt ? <p className="text-[#666666] text-sm mt-2 line-clamp-3">{a.excerpt}</p> : null}
                    <p className="text-xs text-[#666666] mt-3">By {a.author}{a.role ? `, ${a.role}` : ''}{a.readTime ? ` · ${a.readTime}` : ''}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Spotlight */}
      {spotlights.length > 0 && (
        <section className="section-pad py-16 bg-[#f4f4f4]">
          <div className="max-w-[1500px] mx-auto">
            <SectionTitle eyebrow="Partner voices" title="Spotlight" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {spotlights.map((s, i) => (
                <Reveal key={s.org} delay={i * 60}>
                  <div className="bg-white rounded-2xl p-6 h-full border-t-4" style={{ borderColor: s.accent }}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-display text-lg text-white" style={{ backgroundColor: s.accent }}>{s.org.charAt(0)}</div>
                    <h3 className="font-display text-2xl mt-4">{s.org}</h3>
                    <p className="text-xs text-[#666666] uppercase tracking-wider mt-1">{s.full}</p>
                    <p className="text-[#111111] text-sm mt-3 leading-relaxed">{s.blurb}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What Made News */}
      {whatMadeNews.length > 0 && (
        <section className="section-pad py-16">
          <div className="max-w-[1500px] mx-auto">
            <SectionTitle eyebrow="The month in headlines" title="What Made News" />
            <div className="grid md:grid-cols-2 gap-x-12">
              {whatMadeNews.map((n, i) => (
                <Reveal key={i} delay={(i % 2) * 60}>
                  <div className="flex items-start gap-4 py-4 border-b border-[#e5e5e5]">
                    <span className="font-display text-2xl text-[#e5e5e5] w-8 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <span className="text-[0.62rem] font-bold uppercase tracking-wider text-[#D01919]">{n.tag}</span>
                      <p className="text-[#111111] mt-1">{n.headline}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Industry Report */}
      {industryReport && (
        <section className="section-pad py-20 bg-[#111111] text-white">
          <div className="max-w-[1100px] mx-auto">
            <Reveal>
              <div>
                {industryReport.eyebrow ? <p className="eyebrow text-[#D01919] mb-4">{industryReport.eyebrow}</p> : null}
                <h2 className="font-display text-4xl md:text-5xl leading-tight">{industryReport.title}</h2>
                {industryReport.summary ? <p className="text-white/70 text-lg mt-5">{industryReport.summary}</p> : null}
                {industryReport.stats && industryReport.stats.length ? (
                  <div className="grid grid-cols-3 gap-6 mt-8">
                    {industryReport.stats.map((s) => (
                      <div key={s.label} className="border-l border-white/20 pl-4">
                        <p className="font-display text-3xl md:text-4xl text-[#D01919]">{s.value}</p>
                        <p className="text-white/60 text-xs mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Enersider Brief */}
      {enersiderBrief && enersiderBrief.points && enersiderBrief.points.length > 0 && (
        <section className="section-pad py-20">
          <div className="max-w-[1100px] mx-auto">
            <Reveal>
              {enersiderBrief.eyebrow ? <p className="eyebrow text-[#D01919] mb-4">{enersiderBrief.eyebrow}</p> : null}
              <h2 className="font-display text-4xl md:text-5xl mb-8">{enersiderBrief.title}</h2>
              <div className="space-y-0">
                {enersiderBrief.points.map((p, i) => (
                  <div key={i} className="flex items-baseline gap-5 py-5 border-b border-[#e5e5e5]">
                    <span className="font-display text-xl text-[#D01919]">0{i + 1}</span>
                    <p className="text-lg text-[#111111]">{p}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Newsmaker */}
      {newsmaker && (
        <section className="section-pad py-16">
          <div className="max-w-[1500px] mx-auto">
            <Reveal>
              <div className="bg-[#f4f4f4] rounded-3xl" data-testid="newsmaker-card">
                <div className="p-8 md:p-12 max-w-4xl">
                  {newsmaker.eyebrow ? <p className="eyebrow text-[#D01919] mb-4">{newsmaker.eyebrow}</p> : null}
                  {newsmaker.quote ? <><Quote size={40} className="text-[#D01919]" /><p className="font-display text-2xl md:text-3xl leading-snug mt-3">{newsmaker.quote}</p></> : null}
                  {newsmaker.bio ? <p className="text-[#666666] mt-6">{newsmaker.bio}</p> : null}
                  <div className="mt-6">
                    <p className="font-display text-2xl">{newsmaker.name}</p>
                    <p className="text-sm text-[#666666]">{newsmaker.designation}{newsmaker.org ? `, ${newsmaker.org}` : ''}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <FlipbookModal open={!!readUrl} onClose={() => setReadUrl(null)} url={readUrl} title={readTitle} />
    </div>
  );
}
